import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Church, LayoutDashboard, ListChecks, Printer, LogOut, Menu, X } from 'lucide-react';
import { logout, currentUser } from '../lib/auth';

const NAV = [
  { to: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
  { to: '/app/categories', label: 'Categories', icon: ListChecks, testid: 'nav-categories' },
  { to: '/app/balance-sheet', label: 'Balance Sheet', icon: Printer, testid: 'nav-balance-sheet' },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const user = currentUser();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--gcbc-bg)]">
      {/* Royal blue header + red accent line */}
      <header className="no-print sticky top-0 z-40" data-testid="app-header">
        <div className="bg-[var(--gcbc-royal)] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
            <NavLink to="/app/dashboard" className="flex items-center gap-3 group" data-testid="header-logo-link">
              <div className="w-10 h-10 rounded-xl bg-white/12 border border-white/25 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <Church className="w-5 h-5 text-white" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-lg sm:text-xl font-bold tracking-tight">Grants Creek Baptist Church</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-blue-100">Finances · 2026</div>
              </div>
            </NavLink>

            {/* Desktop nav tabs */}
            <nav className="hidden md:flex items-center gap-1" data-testid="header-nav-desktop">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-colors ${
                      isActive
                        ? 'bg-white text-[var(--gcbc-royal-deep)] shadow'
                        : 'text-white hover:bg-white/12'
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <span className="text-sm text-blue-100" data-testid="header-user-name">{user?.name || 'Admin'}</span>
              <button
                onClick={handleLogout}
                data-testid="header-logout-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/12 hover:bg-white/22 text-white text-sm font-medium border border-white/25 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>

            <button
              className="md:hidden p-2 rounded-lg bg-white/10 border border-white/25 text-white"
              onClick={() => setOpen((v) => !v)}
              data-testid="header-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile nav */}
          {open && (
            <div className="md:hidden border-t border-white/15 pb-3">
              <div className="max-w-7xl mx-auto px-4 pt-3 flex flex-col gap-2" data-testid="header-nav-mobile">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 ${
                        isActive ? 'bg-white text-[var(--gcbc-royal-deep)]' : 'text-white bg-white/8'
                      }`
                    }
                  >
                    <n.icon className="w-4 h-4" />
                    {n.label}
                  </NavLink>
                ))}
                <button
                  onClick={handleLogout}
                  className="mt-1 px-3 py-2 rounded-lg bg-[var(--gcbc-red)] text-white text-sm font-semibold flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="gcbc-accent-line" aria-hidden="true"></div>
      </header>

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>

      <footer className="no-print border-t border-slate-200 bg-white" data-testid="app-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-[var(--gcbc-royal-deep)]">GCBC finances app 2026</span>
            <span className="mx-2 text-slate-300">·</span>
            Grants Creek Baptist Church · Stewarding what the Lord provides.
          </div>
          <div className="text-xs text-slate-500">Made with care · Psalm 24:1</div>
        </div>
      </footer>
    </div>
  );
}
